import { Entity } from '@api';
import { ButtonGroup } from '@design/components/button-group/ButtonGroup';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { queryKeys } from '@terminal/routes/query-params';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { BrandsAndProductsTab } from './components/brands-and-products-tab/BrandsAndProductsTab';
import { CompetitorsTab } from './components/competitors-tab/CompetitorsTab';
import { GeographicExposureTab } from './components/geographic-exposure-tab/GeographicExposureTab';
import { OverviewTab } from './components/overview-tab/OverviewTab';
import { ThemeExposureTab } from './components/theme-exposure-tab/ThemeExposureTab';
import { useKnowledgeGraphData } from './hooks/useKnowledgeGraphData';
import styles from './KnowledgeGraphWidget.module.scss';

export enum KnowledgeGraphTabType {
  Overview = 'overview',
  ThemeExposure = 'themeExposure',
  BrandsAndProducts = 'brandsAndProducts',
  Competitors = 'competitors',
  GeographicExposure = 'geographicExposure',
}

interface KnowledgeGraphWidgetProps {
  entity: Entity;
}

export const KnowledgeGraphWidget = ({ entity }: KnowledgeGraphWidgetProps) => {
  const { t } = useTranslation(['widget', 'scenario']);
  const { params, setQueryParams } = useQueryParams();
  const { trackConnectionsTabSelected } = usePosthogEvents();

  const {
    hasThemeData,
    hasBrandsAndProductsData,
    hasCompetitorsData,
    hasGeographicData,
    isFetching,
  } = useKnowledgeGraphData({
    entityTag: entity.tag,
  });

  const hasAnyData =
    hasThemeData ||
    hasBrandsAndProductsData ||
    hasCompetitorsData ||
    hasGeographicData;

  const knowledgeGraphTabs = useMemo(() => {
    const tabs = [];

    if (hasAnyData) {
      tabs.push({
        tabLabel: t('assetOverview.knowledgeGraph.subtab.overview'),
        tabType: KnowledgeGraphTabType.Overview,
      });
    } else {
      return [];
    }

    if (hasThemeData) {
      tabs.push({
        tabLabel: t('assetOverview.knowledgeGraph.subtab.theme'),
        tabType: KnowledgeGraphTabType.ThemeExposure,
      });
    }

    if (hasBrandsAndProductsData) {
      tabs.push({
        tabLabel: t('assetOverview.knowledgeGraph.subtab.brandsAndProducts'),
        tabType: KnowledgeGraphTabType.BrandsAndProducts,
      });
    }

    if (hasCompetitorsData) {
      tabs.push({
        tabLabel: t('assetOverview.knowledgeGraph.subtab.competitors'),
        tabType: KnowledgeGraphTabType.Competitors,
      });
    }

    if (hasGeographicData) {
      tabs.push({
        tabLabel: t('assetOverview.knowledgeGraph.subtab.geographic'),
        tabType: KnowledgeGraphTabType.GeographicExposure,
      });
    }

    return tabs;
  }, [
    t,
    hasAnyData,
    hasThemeData,
    hasBrandsAndProductsData,
    hasCompetitorsData,
    hasGeographicData,
  ]);

  const renderTabContent = (tab: string) => {
    switch (tab) {
      case KnowledgeGraphTabType.Overview:
        return (
          <OverviewTab
            entity={entity}
            hasThemeData={hasThemeData}
            hasBrandsAndProductsData={hasBrandsAndProductsData}
            hasCompetitorsData={hasCompetitorsData}
            hasGeographicData={hasGeographicData}
          />
        );
      case KnowledgeGraphTabType.ThemeExposure:
        return <ThemeExposureTab entity={entity} />;
      case KnowledgeGraphTabType.BrandsAndProducts:
        return <BrandsAndProductsTab entity={entity} />;
      case KnowledgeGraphTabType.Competitors:
        return <CompetitorsTab entity={entity} />;
      case KnowledgeGraphTabType.GeographicExposure:
        return <GeographicExposureTab entity={entity} />;
      default:
        return null;
    }
  };

  const onTabChange = (tab: KnowledgeGraphTabType) => {
    trackConnectionsTabSelected(POSTHOG_TRACKING_SOURCES.ASSET_OVERVIEW, tab, [
      entity.tag,
    ]);
    setQueryParams({
      [queryKeys.activeSubTab]: tab,
      [queryKeys.geoSubTab]: undefined,
    });
  };

  const requestedTab =
    params[queryKeys.activeSubTab] ?? KnowledgeGraphTabType.Overview;
  const activeTab = knowledgeGraphTabs.some(tab => tab.tabType === requestedTab)
    ? requestedTab
    : (knowledgeGraphTabs[0]?.tabType ?? KnowledgeGraphTabType.Overview);

  const activeIndex = knowledgeGraphTabs.findIndex(
    tab => tab.tabType === activeTab
  );

  if (!isFetching && knowledgeGraphTabs.length === 0) {
    return null;
  }

  return (
    <div className={styles.container}>
      {knowledgeGraphTabs.length > 0 && activeIndex >= 0 && (
        <ButtonGroup
          className={styles.styledButtonGroup}
          labels={knowledgeGraphTabs.map(t => t.tabLabel)}
          activeIndex={activeIndex}
          onClick={tab => onTabChange(knowledgeGraphTabs[tab].tabType)}
        />
      )}
      {renderTabContent(activeTab)}
    </div>
  );
};
